import { HttpClient, HttpContext, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";

import {
	EnvironmentConfig,
	ENV_CONFIG
} from "@core/environments/environment-config";
import { LocalStorageService } from "@core/services/storage/local-storage.service";
import { HttpParamsConfig } from "@shared/schemas/http-params-config";

interface NewSaveUpdateNamedParameters<T> {
	path: string;
	object: T & { id?: string; hashedFingerprint?: string };
	patch?: boolean;
}

interface SaveUpdateNamedParameters<T> {
	path: string;
	object: T;
	patch?: boolean;
}

interface getAllNamedParameters {
	path: string;
	params?: { [param: string]: string | number | boolean };
	context?: HttpContext;
}

interface DeleteNamedParameters {
	path: string;
	id: number;
}

@Injectable({
	providedIn: "root"
})
export class BaseCrudBffService {
	API_BFF_URL = "";

	constructor(
		protected http: HttpClient,
		private localStorageService: LocalStorageService,
		@Inject(ENV_CONFIG) private config: EnvironmentConfig
	) {
		this.API_BFF_URL = `${config.environment.API_BFF}`;
	}

	getAll<T>({ path, params, context }: getAllNamedParameters): Observable<T> {
		let allParams = new HttpParams().set(
			"hashedFingerprint",
			this.getHashedFingerPrint()
		);

		if (params) {
			allParams = allParams.appendAll(params);
		}

		return this.http.get<T>(`${this.API_BFF_URL}/${path}`, {
			params: allParams,
			withCredentials: true,
			context: context ?? undefined
		});
	}

	getById<T>(path: string, id: number): Observable<T> {
		return this.http.get<T>(
			`${
				this.API_BFF_URL
			}/${path}/${id}?hashedFingerprint=${this.getHashedFingerPrint()}`,
			{
				withCredentials: true
			}
		);
	}

	newSave<T>({
		path,
		object,
		patch = false
	}: NewSaveUpdateNamedParameters<T>) {
		object.hashedFingerprint = this.getHashedFingerPrint();

		if (object.id) {
			return this.update({ path, object, patch });
		}

		return this.create(path, object);
	}

	save<T extends { id?: string | number; hashedFingerprint?: string }>({
		path,
		object,
		patch
	}: SaveUpdateNamedParameters<T>): Observable<T> {
		object.hashedFingerprint = this.getHashedFingerPrint();

		if (object.id) {
			return this.update({ path, object, patch });
		} else {
			return this.create(path, object);
		}
	}

	private update<T extends { id?: string | number }>({
		path,
		object,
		patch
	}: SaveUpdateNamedParameters<T>) {
		return patch
			? this.http.patch<T>(
					`${this.API_BFF_URL}/${path}/${Number(object.id)}`,
					object,
					{
						withCredentials: true
					}
				)
			: this.http.put<T>(
					`${this.API_BFF_URL}/${path}/${Number(object.id)}`,
					object,
					{
						withCredentials: true
					}
				);
	}

	private create<T>(path: string, object: T) {
		return this.http.post<T>(`${this.API_BFF_URL}/${path}`, object, {
			withCredentials: true
		});
	}

	delete<T extends { id?: string | number }>({
		path,
		id
	}: DeleteNamedParameters): Observable<T> {
		return this.http.delete<T>(
			`${
				this.API_BFF_URL
			}/${path}/${id}?hashedFingerprint=${this.getHashedFingerPrint()}`,
			{
				withCredentials: true
			}
		);
	}

	deleteWithBody<T>(
		path: string,
		id: number,
		object?: unknown
	): Observable<T> {
		return this.http.delete<T>(
			`${
				this.API_BFF_URL
			}/${path}/${id}?hashedFingerprint=${this.getHashedFingerPrint()}`,
			{
				body: object,
				withCredentials: true
			}
		);
	}

	private getHashedFingerPrint(): string {
		return this.localStorageService.get("fingerprint") ?? "";
	}

	getAllFiltersParams(httpParamsConfig?: HttpParamsConfig): {
		[param: string]: string | number;
	} {
		if (!httpParamsConfig) return {};

		const { fields, ...filters } = httpParamsConfig;
		const fieldParams =
			fields?.reduce(
				(acc, field) => ({ ...acc, [field.key]: field.value }),
				{}
			) || {};

		return { ...fieldParams, ...filters };
	}
}
